import { CUSTOMER_STAGE_LABEL_OVERRIDES } from "constants/clientProgress";
import {
  STAGE_KEY_MISSING_INFORMATION,
  STAGE_KEY_PRICING,
} from "constants/clientProgressStageKeys";
import { PAID_TASK } from "constants/task";
import { AMENDMENT_FILED } from "constants/taxValues";
import { ClientProgress, TaskStepDetails } from "types/Models/task";

type GetClientStageLabelArgs = {
  key: string;
  label: string;
  paymentStatus?: string | null;
};

export const getClientStageLabel = ({
  key,
  label,
  paymentStatus,
}: GetClientStageLabelArgs): string => {
  const isUnpaidPricing =
    key === STAGE_KEY_PRICING && paymentStatus !== PAID_TASK;

  if (isUnpaidPricing || label === AMENDMENT_FILED) return label;

  return CUSTOMER_STAGE_LABEL_OVERRIDES[key] ?? label;
};

const buildAmendmentStepDetails = (
  clientProgress: ClientProgress,
  paymentStatus?: string | null
): TaskStepDetails[] => {
  const visitedKeys = new Set(
    clientProgress.completed_states.map((s) => s.key)
  );

  return clientProgress.stages
    .filter(
      (s) =>
        s.key !== STAGE_KEY_MISSING_INFORMATION ||
        s.is_current ||
        visitedKeys.has(s.key)
    )
    .map((s) => ({
      name: getClientStageLabel({ key: s.key, label: s.label, paymentStatus }),
      key: s.key,
      isCompleted: s.is_completed && !s.is_current,
      isCurrent: s.is_current,
      isIncomplete: !s.is_completed && !s.is_current,
      isOnHold: s.is_on_hold,
      description: s.description ?? undefined,
    }));
};

export function buildClientProgressStepDetails(
  clientProgress: ClientProgress,
  paymentStatus?: string | null
): TaskStepDetails[] {
  if (clientProgress.amendment_number) {
    return buildAmendmentStepDetails(clientProgress, paymentStatus);
  }

  const { completed_states, current_key, stages } = clientProgress;
  const lastCurrentIndex = completed_states
    .map((s) => s.key)
    .lastIndexOf(current_key);
  const completedKeys = new Set(completed_states.map((s) => s.key));

  return [
    ...completed_states.map((s, i) => ({
      name: getClientStageLabel({ key: s.key, label: s.label, paymentStatus }),
      key: s.key,
      isCompleted: i !== lastCurrentIndex,
      isCurrent: i === lastCurrentIndex,
      isIncomplete: false,
      isOnHold: s.is_on_hold,
      description: s.description ?? undefined,
    })),
    ...stages
      .filter(
        (s) =>
          !s.is_completed &&
          !s.is_current &&
          !completedKeys.has(s.key) &&
          s.key !== STAGE_KEY_MISSING_INFORMATION
      )
      .map((s) => ({
        name: getClientStageLabel({
          key: s.key,
          label: s.label,
          paymentStatus,
        }),
        key: s.key,
        isCompleted: false,
        isCurrent: false,
        isIncomplete: true,
        isOnHold: false,
        description: s.description ?? undefined,
      })),
  ];
}
